import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Lock,
  ArrowRight,
  Sparkles,
  Building2,
  UserCheck,
  ShieldCheck,
  Globe,
  MapPin,
  CheckCircle2,
  AlertCircle,
  Loader2,
  UploadCloud,
  X,
  Trash2,
  Image as ImageIcon,
  Copy,
  ExternalLink,
  Check,
} from "lucide-react";
import { toast } from "react-toastify";
import swal from "sweetalert";
import methodModel from "../methods";
import loader from "../loader";
import ApiClient from "../api/apiClient";
import FormControl from "../../components/common/FormControl";
import {
  getTenantAdminUrl,
  getTenantFrontendUrl,
  getBaseDomain,
} from "../../utils/domain";

export default function Signup() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);

  // Tenant Admin Form State
  const [tenantForm, setTenantForm] = useState({
    company_name: "",
    subdomain: "",
    portal_title: "",
    adminFirstName: "",
    adminLastName: "",
    adminEmail: "",
    adminPassword: "",
    adminPhone: "",
    support_email: "",
    contact_email: "",
    support_phone: "",
    contact_phone: "",
    street: "",
    city: "",
    state: "",
    country: "USA",
    zipCode: "",
    logo_url: "",
  });

  const [errors, setErrors] = useState({});

  // Subdomain live availability state
  const [subdomainStatus, setSubdomainStatus] = useState({
    checking: false,
    available: null, // true | false | null
    message: "",
  });

  // Helper to slugify string for subdomain
  const slugify = (text) => {
    return String(text || "")
      .toLowerCase()
      .trim()
      .replace(/[\s_]+/g, "-")
      .replace(/[^\w-]+/g, "")
      .replace(/--+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [copiedAdmin, setCopiedAdmin] = useState(false);
  const [copiedFrontend, setCopiedFrontend] = useState(false);

  const copyToClipboard = (text, type) => {
    if (!text) return;
    navigator.clipboard?.writeText(text);
    if (type === "admin") {
      setCopiedAdmin(true);
      setTimeout(() => setCopiedAdmin(false), 2000);
    } else {
      setCopiedFrontend(true);
      setTimeout(() => setCopiedFrontend(false), 2000);
    }
    toast.success("Domain URL copied to clipboard!");
  };

  const getAdminPortalUrl = (sub) => {
    if (!sub) return "";
    return getTenantAdminUrl(slugify(sub));
  };

  const getFrontendPortalUrl = (sub) => {
    if (!sub) return "";
    return getTenantFrontendUrl(slugify(sub));
  };

  // Check Subdomain Availability via API using server URL
  const checkSubdomainAvailability = async (subdomainVal) => {
    const clean = slugify(subdomainVal);
    if (!clean || clean.length < 2) {
      setSubdomainStatus({ checking: false, checked: false, available: true, message: "" });
      return;
    }

    setSubdomainStatus((prev) => ({ ...prev, checking: true }));

    try {
      const res = await ApiClient.get(
        "tenants/check-subdomain",
        { subdomain: clean },
        "",
        true
      );

      if (res?.success && res?.data) {
        const isAvail = Boolean(res.data.available);
        const reasonMsg =
          res.data.reason ||
          res.message ||
          (isAvail ? "Subdomain is available." : "Subdomain is already taken.");
        setSubdomainStatus({
          checking: false,
          checked: true,
          available: isAvail,
          message: reasonMsg,
        });
        if (!isAvail) {
          setErrors((prev) => ({ ...prev, subdomain: reasonMsg }));
        } else {
          setErrors((prev) => ({ ...prev, subdomain: "" }));
        }
      } else {
        const fallbackMsg = res?.message || "Subdomain is not available.";
        setSubdomainStatus({
          checking: false,
          checked: true,
          available: false,
          message: fallbackMsg,
        });
        setErrors((prev) => ({ ...prev, subdomain: fallbackMsg }));
      }
    } catch (err) {
      const errMsg =
        err?.response?.data?.message ||
        err?.response?.data?.data?.reason ||
        "Subdomain is already taken.";
      setSubdomainStatus({
        checking: false,
        checked: true,
        available: false,
        message: errMsg,
      });
      setErrors((prev) => ({ ...prev, subdomain: errMsg }));
    }
  };

  // Debounced effect to check subdomain availability on change
  useEffect(() => {
    const clean = slugify(tenantForm.subdomain);
    if (!clean || clean.length < 2) {
      setSubdomainStatus({ checking: false, available: null, message: "" });
      return;
    }

    const timer = setTimeout(() => {
      checkSubdomainAvailability(clean);
    }, 400);

    return () => clearTimeout(timer);
  }, [tenantForm.subdomain]);

  // Handle Logo Upload matching Admin Tenant module
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file (PNG, JPG, SVG).");
      return;
    }

    setLogoUploading(true);
    loader(true);

    try {
      const res = await ApiClient.postFormData("upload/logo", {
        file: file,
      });

      if (res?.success || res?.fileUrl || res?.location || res?.data) {
        let uploadedUrl =
          res?.data?.fileUrl ||
          res?.fileUrl ||
          res?.data?.filePath ||
          res?.filePath ||
          res?.data?.url ||
          res?.location;

        if (uploadedUrl && typeof uploadedUrl === "string") {
          if (window.location.protocol === "https:" && uploadedUrl.startsWith("http://")) {
            uploadedUrl = uploadedUrl.replace("http://", "https://");
          }
          setTenantForm((prev) => ({ ...prev, logo_url: uploadedUrl }));
          toast.success("Logo uploaded successfully.");
        } else {
          toast.error("Unable to get uploaded logo URL.");
        }
      } else {
        toast.error(res?.message || "Failed to upload logo.");
      }
    } catch (err) {
      console.error("Logo upload error:", err);
      toast.error("Failed to upload logo.");
    } finally {
      setLogoUploading(false);
      loader(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Real-time Tenant Form Change Handler
  const handleTenantChange = (key, val) => {
    const raw = typeof val === "object" && val?.target !== undefined ? val.target.value : val;
    const str = String(raw || "");

    setTenantForm((prev) => {
      const updated = { ...prev, [key]: str };
      if (key === "company_name" && !prev.subdomainManuallyEdited) {
        const generatedSub = slugify(str);
        updated.subdomain = generatedSub;
        if (!prev.portal_title) {
          updated.portal_title = `${str.trim()} Portal`;
        }
      }
      return updated;
    });

    if (submitted || errors[key]) {
      validateField(key, str);
    }
  };

  const validateField = (key, val) => {
    const trimmed = String(val || "").trim();

    if (key === "adminEmail") {
      if (!trimmed) {
        setErrors((prev) => ({ ...prev, adminEmail: "Please enter email address." }));
      } else {
        const check = methodModel.validateEmail(trimmed);
        setErrors((prev) => ({ ...prev, adminEmail: check.success ? "" : check.message }));
      }
    } else if (key === "adminPassword") {
      if (!val) {
        setErrors((prev) => ({ ...prev, adminPassword: "Please enter password." }));
      } else {
        const check = methodModel.validateLoginPassword(val);
        setErrors((prev) => ({ ...prev, adminPassword: check.success ? "" : check.message }));
      }
    } else if (key === "company_name") {
      if (!trimmed) {
        setErrors((prev) => ({ ...prev, company_name: "Company name is required." }));
      } else {
        setErrors((prev) => ({ ...prev, company_name: "" }));
      }
    } else if (key === "subdomain") {
      if (!trimmed) {
        setErrors((prev) => ({ ...prev, subdomain: "Subdomain is required." }));
      } else if (subdomainStatus.available === false) {
        setErrors((prev) => ({ ...prev, subdomain: subdomainStatus.message || "Subdomain is already taken." }));
      } else {
        setErrors((prev) => ({ ...prev, subdomain: "" }));
      }
    } else if (key === "adminFirstName") {
      if (!trimmed) {
        setErrors((prev) => ({ ...prev, adminFirstName: "First name is required." }));
      } else {
        setErrors((prev) => ({ ...prev, adminFirstName: "" }));
      }
    } else if (key === "adminLastName") {
      if (!trimmed) {
        setErrors((prev) => ({ ...prev, adminLastName: "Last name is required." }));
      } else {
        setErrors((prev) => ({ ...prev, adminLastName: "" }));
      }
    }
  };

  // Submit Handler for Tenant Signup
  const handleTenantSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);

    let newErrors = {};
    if (!tenantForm.company_name.trim()) newErrors.company_name = "Company name is required.";
    if (!tenantForm.subdomain.trim()) newErrors.subdomain = "Subdomain is required.";
    if (subdomainStatus.available === false) newErrors.subdomain = "Subdomain is already taken.";
    if (!tenantForm.adminFirstName.trim()) newErrors.adminFirstName = "First name is required.";
    if (!tenantForm.adminLastName.trim()) newErrors.adminLastName = "Last name is required.";

    const emailCheck = methodModel.validateEmail(tenantForm.adminEmail.trim());
    if (!emailCheck.success) newErrors.adminEmail = emailCheck.message;

    const pwdCheck = methodModel.validateLoginPassword(tenantForm.adminPassword);
    if (!pwdCheck.success) newErrors.adminPassword = pwdCheck.message;

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Please fill in all required fields accurately.");
      return;
    }

    setErrors({});
    setLoading(true);
    loader(true);

    const payload = {
      company_name: tenantForm.company_name.trim(),
      subdomain: tenantForm.subdomain.trim().toLowerCase(),
      portal_title: tenantForm.portal_title.trim() || `${tenantForm.company_name.trim()} Portal`,
      adminFirstName: tenantForm.adminFirstName.trim(),
      adminLastName: tenantForm.adminLastName.trim(),
      adminEmail: tenantForm.adminEmail.trim(),
      adminPassword: tenantForm.adminPassword,
      adminPhone: tenantForm.adminPhone.trim() || "+1-800-555-0199",
      support_email: tenantForm.support_email.trim() || tenantForm.adminEmail.trim(),
      contact_email: tenantForm.contact_email.trim() || tenantForm.adminEmail.trim(),
      support_phone: tenantForm.support_phone.trim() || tenantForm.adminPhone.trim() || "+1-800-555-0100",
      contact_phone: tenantForm.contact_phone.trim() || tenantForm.adminPhone.trim() || "+1-800-555-0101",
      address: {
        street: tenantForm.street.trim() || "100 Industrial Parkway",
        city: tenantForm.city.trim() || "Dallas",
        state: tenantForm.state.trim() || "TX",
        country: tenantForm.country.trim() || "USA",
        zipCode: tenantForm.zipCode.trim() || "75001",
      },
      logo: tenantForm.logo_url.trim() || undefined,
      logo_url: tenantForm.logo_url.trim() || undefined,
    };

    try {
      const res = await ApiClient.post("tenants/signup", payload, "", true);

      loader(false);
      setLoading(false);

      if (res?.success || res?.status === "success" || res?.id || res?.data) {
        const adminUrl = getTenantAdminUrl(payload.subdomain);
        const frontendUrl = getTenantFrontendUrl(payload.subdomain);
        setSuccessData({
          company_name: payload.company_name,
          subdomain: payload.subdomain,
          adminEmail: payload.adminEmail,
          adminUrl,
          frontendUrl,
        });
        setSuccessModalOpen(true);
      } else {
        const errMsg = res?.error?.message || res?.message || "Failed to register portal.";
        toast.error(errMsg);
      }
    } catch (err) {
      loader(false);
      setLoading(false);
      const errMsg =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to register business portal. Please try again.";
      toast.error(errMsg);
    }
  };

  const logoSrc = "/assets/img/logo.png";

  return (
    <div className="w-screen h-screen overflow-hidden bg-white flex flex-col font-sans">
      <div className="flex-1 flex flex-col lg:flex-row w-full h-full overflow-hidden">
        {/* ======================================================== */}
        {/* LEFT BRAND SHOWCASE PANEL (Hero Canvas)                  */}
        {/* ======================================================== */}
        <div className="hidden lg:flex lg:w-1/2 xl:w-[48%] h-full bg-gradient-to-br from-[#062037] via-[#0b3860] to-[#115B9D] p-8 xl:p-12 relative flex-col justify-between overflow-hidden select-none">
          <div
            className="absolute inset-0 opacity-10 pointer-events-none"
            style={{
              backgroundImage: "radial-gradient(#ffffff 1px, transparent 1px)",
              backgroundSize: "24px 24px",
            }}
          />

          {/* Top Brand Tag Header */}
          <div className="relative z-10 flex items-center justify-between">
            <div className="inline-flex items-center gap-2.5 px-3.5 py-2 rounded-xl bg-white/10 backdrop-blur-md border border-white/20">
              <div className="w-7 h-7 rounded-lg bg-white p-1 flex items-center justify-center shadow-sm">
                <img
                  src={logoSrc}
                  alt="Charles S. Smith & Associates"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="leading-tight">
                <span className="block text-[10px] font-bold uppercase tracking-wider text-sky-200">
                  E-Commerce Suite
                </span>
                <span className="block text-xs font-semibold text-white tracking-tight">
                  Charles S. Smith & Associates
                </span>
              </div>
            </div>

            {/* Live Status Pill */}
            <div className="hidden xl:inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 text-xs font-medium backdrop-blur-sm">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
              </span>
              Systems Operational
            </div>
          </div>

          {/* Center Showcase Content */}
          <div className="relative z-10 my-auto py-4 xl:py-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-400/15 border border-sky-300/30 text-sky-200 text-xs font-semibold uppercase tracking-wider mb-3">
              <Sparkles className="w-3.5 h-3.5 text-sky-300" />
              TENANT & COMMERCE ONBOARDING
            </div>

            <h1 className="text-2xl xl:text-3xl 2xl:text-4xl font-extrabold text-white tracking-tight leading-tight">
              Launch Your Branded{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-300 via-sky-200 to-white">
                Equipment & Commerce Portal
              </span>
            </h1>

            <p className="mt-2.5 text-xs xl:text-sm text-sky-100 leading-relaxed max-w-lg font-normal">
              Register your business, claim your dedicated subdomain, and provision your tenant administrator credentials in minutes.
            </p>

            {/* Feature Cards Grid */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-2.5 mt-5">
              <div className="bg-white/10 backdrop-blur-md p-3 rounded-xl border border-white/15 hover:bg-white/15 transition-all">
                <div className="w-7 h-7 rounded-lg bg-sky-400/20 flex items-center justify-center text-sky-300 mb-2">
                  <Globe className="w-3.5 h-3.5" />
                </div>
                <h2 className="text-xs font-bold text-white tracking-wide">
                  Custom Subdomain
                </h2>
                <p className="text-[11px] text-sky-100/90 mt-0.5 leading-tight">
                  Your own URL (e.g. business.domain.com).
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-md p-3 rounded-xl border border-white/15 hover:bg-white/15 transition-all">
                <div className="w-7 h-7 rounded-lg bg-indigo-400/20 flex items-center justify-center text-indigo-300 mb-2">
                  <Building2 className="w-3.5 h-3.5" />
                </div>
                <h2 className="text-xs font-bold text-white tracking-wide">
                  Store Control
                </h2>
                <p className="text-[11px] text-sky-100/90 mt-0.5 leading-tight">
                  Manage products, quotes, clients & orders.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-md p-3 rounded-xl border border-white/15 hover:bg-white/15 transition-all">
                <div className="w-7 h-7 rounded-lg bg-emerald-400/20 flex items-center justify-center text-emerald-300 mb-2">
                  <ShieldCheck className="w-3.5 h-3.5" />
                </div>
                <h2 className="text-xs font-bold text-white tracking-wide">
                  Isolated Data
                </h2>
                <p className="text-[11px] text-sky-100/90 mt-0.5 leading-tight">
                  Secure tenant isolation & SSL encryption.
                </p>
              </div>
            </div>
          </div>

          {/* Bottom Trust & Heritage Footer */}
          <div className="relative z-10 pt-4 border-t border-white/10 flex items-center justify-between text-xs text-sky-100">
            <span className="font-semibold text-white">
              Serving the Americas Since 1976
            </span>
            <span className="flex items-center gap-1.5 text-sky-200 font-medium">
              <Lock className="w-3.5 h-3.5 text-sky-300" /> 256-Bit SSL Encrypted
            </span>
          </div>
        </div>

        {/* ======================================================== */}
        {/* RIGHT REGISTRATION FORM PANEL (Scrollable Clean Canvas) */}
        {/* ======================================================== */}
        <div className="w-full lg:w-1/2 xl:w-[52%] h-full flex flex-col justify-between px-6 py-6 sm:px-10 sm:py-8 lg:px-10 xl:px-14 bg-white overflow-y-auto">
          <div className="w-full max-w-[540px] mx-auto my-auto py-2">
            {/* Header with Logo */}
            <div className="text-center mb-5">
              <img
                src={logoSrc}
                alt="Platform Logo"
                className="h-10 w-auto object-contain mx-auto mb-2"
              />
              <h2 className="text-2xl font-bold text-slate-900 tracking-tight">
                Create Business Portal
              </h2>
              <p className="text-xs text-slate-700 mt-1 leading-relaxed font-medium">
                Configure your business subdomain and administrator access.
              </p>
            </div>

            {/* ==================================================== */}
            {/* TENANT SIGNUP FORM                                   */}
            {/* ==================================================== */}
            <form onSubmit={handleTenantSubmit} noValidate className="space-y-4">
              {/* 1. Organization & Branding Information */}
              <div className="border border-slate-200 rounded-xl p-3.5 bg-slate-50/50 space-y-3">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-800 flex items-center gap-1.5 border-b border-slate-200 pb-1.5">
                  <Building2 className="w-3.5 h-3.5 text-[#115B9D]" />
                  Company & Subdomain Information
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <FormControl
                    type="text"
                    name="company_name"
                    label="Company Name"
                    required={true}
                    placeholder="Apex Industrial Equipment Inc"
                    value={tenantForm.company_name}
                    error={errors.company_name}
                    onChange={(val) => handleTenantChange("company_name", val)}
                    onBlur={() => validateField("company_name", tenantForm.company_name)}
                  />

                  <div>
                    <FormControl
                      type="text"
                      name="subdomain"
                      label="Subdomain"
                      required={true}
                      placeholder="e.g. apex"
                      value={tenantForm.subdomain}
                      error={
                        subdomainStatus.checked && !subdomainStatus.available
                          ? subdomainStatus.message
                          : errors.subdomain
                      }
                      onChange={(val) => {
                        const cleanVal = slugify(val);
                        setTenantForm((prev) => ({
                          ...prev,
                          subdomain: cleanVal,
                          subdomainManuallyEdited: true,
                        }));
                        if (errors.subdomain) {
                          validateField("subdomain", cleanVal);
                        }
                      }}
                      onBlur={() => validateField("subdomain", tenantForm.subdomain)}
                    />
                    {/* Live Subdomain Status & Preview */}
                    {tenantForm.subdomain && (
                      <div className="mt-2 space-y-2 p-3 rounded-xl bg-slate-50 border border-slate-200 text-xs">
                        {subdomainStatus.checking ? (
                          <div className="text-slate-500 font-medium flex items-center gap-1.5">
                            <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />
                            <span>Checking domain availability...</span>
                          </div>
                        ) : subdomainStatus.checked && !subdomainStatus.available ? (
                          <div className="flex items-center gap-1.5 text-rose-700 font-bold">
                            <AlertCircle className="w-3.5 h-3.5 text-rose-600 shrink-0" />
                            <span>{subdomainStatus.message}</span>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-center justify-between text-[11px] font-bold text-slate-500 uppercase tracking-wider pb-1.5 border-b border-slate-200/80">
                              <span>Dynamic Domains Reserved:</span>
                              {subdomainStatus.checked && subdomainStatus.available && (
                                <span className="text-emerald-700 font-bold flex items-center gap-1">
                                  <CheckCircle2 size={12} /> Available
                                </span>
                              )}
                            </div>
                            <div className="space-y-1.5 text-slate-700 font-medium text-[11px]">
                              <div className="flex items-center gap-2 truncate">
                                <span className="px-1.5 py-0.5 rounded bg-blue-100 text-[#115B9D] font-bold text-[10px] shrink-0">
                                  Tenant Admin
                                </span>
                                <span className="font-mono text-slate-900 truncate">
                                  {getAdminPortalUrl(tenantForm.subdomain)}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 truncate">
                                <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold text-[10px] shrink-0">
                                  Customer Store
                                </span>
                                <span className="font-mono text-slate-900 truncate">
                                  {getFrontendPortalUrl(tenantForm.subdomain)}
                                </span>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <FormControl
                  type="text"
                  name="portal_title"
                  label="Portal Title"
                  placeholder="Apex Equipment & Quoting Portal"
                  value={tenantForm.portal_title}
                  onChange={(val) => handleTenantChange("portal_title", val)}
                />

                {/* Upload Logo Option matching Admin Tenant module */}
                <div className="pt-1 border-t border-slate-200">
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-900 mb-2">
                    Organization Logo
                  </label>
                  <div className="flex items-center gap-4">
                    {tenantForm.logo_url ? (
                      <div className="relative group w-20 h-20 rounded-xl bg-white border border-slate-300 p-1.5 flex items-center justify-center shadow-xs">
                        <img
                          src={tenantForm.logo_url}
                          alt="Uploaded Logo Preview"
                          className="max-w-full max-h-full object-contain"
                        />
                        <button
                          type="button"
                          onClick={() => setTenantForm((prev) => ({ ...prev, logo_url: "" }))}
                          className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-rose-600 text-white flex items-center justify-center shadow-md hover:bg-rose-700 transition-colors cursor-pointer"
                          title="Remove logo"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-20 h-20 rounded-xl bg-white border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400">
                        <ImageIcon className="w-6 h-6 mb-1 text-slate-400" />
                        <span className="text-[9px] font-bold uppercase tracking-wider text-slate-500">
                          No Logo
                        </span>
                      </div>
                    )}

                    <div className="space-y-1.5 flex-1">
                      <div className="flex items-center gap-2">
                        <input
                          type="file"
                          ref={fileInputRef}
                          accept="image/*"
                          onChange={handleLogoUpload}
                          className="hidden"
                        />
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={logoUploading}
                          className="inline-flex items-center gap-1.5 px-3.5 h-9 rounded-xl bg-white hover:bg-slate-100 text-slate-800 border border-slate-300 text-xs font-bold transition-all shadow-xs cursor-pointer active:scale-[0.98] disabled:opacity-75"
                        >
                          {logoUploading ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin text-[#115B9D]" />
                          ) : (
                            <UploadCloud className="w-3.5 h-3.5 text-[#115B9D]" />
                          )}
                          <span>
                            {logoUploading
                              ? "Uploading..."
                              : tenantForm.logo_url
                              ? "Replace Logo"
                              : "Upload Logo"}
                          </span>
                        </button>

                        {tenantForm.logo_url && (
                          <button
                            type="button"
                            onClick={() => setTenantForm((prev) => ({ ...prev, logo_url: "" }))}
                            className="inline-flex items-center gap-1 px-2.5 h-9 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-800 border border-rose-200 text-xs font-bold transition-all cursor-pointer"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            <span>Remove</span>
                          </button>
                        )}
                      </div>
                      <p className="text-[11px] text-slate-500">
                        PNG, JPG, or SVG with transparent background (max 5MB).
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 2. Administrator Credentials */}
              <div className="border border-slate-200 rounded-xl p-3.5 bg-slate-50/50 space-y-3">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-800 flex items-center gap-1.5 border-b border-slate-200 pb-1.5">
                  <UserCheck className="w-3.5 h-3.5 text-[#115B9D]" />
                  Tenant Administrator Credentials
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <FormControl
                    type="text"
                    name="adminFirstName"
                    label="Admin First Name"
                    required={true}
                    placeholder="Alex"
                    value={tenantForm.adminFirstName}
                    error={errors.adminFirstName}
                    onChange={(val) => handleTenantChange("adminFirstName", val)}
                    onBlur={() => validateField("adminFirstName", tenantForm.adminFirstName)}
                  />
                  <FormControl
                    type="text"
                    name="adminLastName"
                    label="Admin Last Name"
                    required={true}
                    placeholder="Mercer"
                    value={tenantForm.adminLastName}
                    error={errors.adminLastName}
                    onChange={(val) => handleTenantChange("adminLastName", val)}
                    onBlur={() => validateField("adminLastName", tenantForm.adminLastName)}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <FormControl
                    type="email"
                    name="adminEmail"
                    label="Admin Email"
                    required={true}
                    placeholder="alex.mercer@apexindustrial.com"
                    value={tenantForm.adminEmail}
                    error={errors.adminEmail}
                    onChange={(val) => handleTenantChange("adminEmail", val)}
                    onBlur={() => validateField("adminEmail", tenantForm.adminEmail)}
                  />
                  <FormControl
                    type="phone"
                    name="adminPhone"
                    label="Admin Phone"
                    placeholder="+1 (800) 555-0199"
                    value={tenantForm.adminPhone}
                    onChange={(val) => handleTenantChange("adminPhone", val)}
                  />
                </div>

                <FormControl
                  type="password"
                  name="adminPassword"
                  label="Admin Password"
                  required={true}
                  showEye={true}
                  placeholder="SecurePassword@123"
                  value={tenantForm.adminPassword}
                  error={errors.adminPassword}
                  onChange={(val) => handleTenantChange("adminPassword", val)}
                  onBlur={() => validateField("adminPassword", tenantForm.adminPassword)}
                />
                <p className="text-[10px] text-slate-500">
                  Must be 8-32 characters and include uppercase, lowercase, number, and special symbol.
                </p>
              </div>

              {/* 3. Business Address & Contacts */}
              <div className="border border-slate-200 rounded-xl p-3.5 bg-slate-50/50 space-y-3">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-800 flex items-center gap-1.5 border-b border-slate-200 pb-1.5">
                  <MapPin className="w-3.5 h-3.5 text-[#115B9D]" />
                  Business Address & Contacts
                </div>

                <FormControl
                  type="text"
                  name="street"
                  label="Street Address"
                  placeholder="100 Industrial Parkway, Suite 400"
                  value={tenantForm.street}
                  onChange={(val) => handleTenantChange("street", val)}
                />

                <div className="grid grid-cols-3 gap-2">
                  <FormControl
                    type="text"
                    name="city"
                    label="City"
                    placeholder="Dallas"
                    value={tenantForm.city}
                    onChange={(val) => handleTenantChange("city", val)}
                  />
                  <FormControl
                    type="text"
                    name="state"
                    label="State"
                    placeholder="TX"
                    value={tenantForm.state}
                    onChange={(val) => handleTenantChange("state", val)}
                  />
                  <FormControl
                    type="text"
                    name="zipCode"
                    label="Zip Code"
                    placeholder="75001"
                    value={tenantForm.zipCode}
                    onChange={(val) => handleTenantChange("zipCode", val)}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                  <FormControl
                    type="email"
                    name="support_email"
                    label="Support Email"
                    placeholder="support@apexindustrial.com"
                    value={tenantForm.support_email}
                    onChange={(val) => handleTenantChange("support_email", val)}
                  />
                  <FormControl
                    type="phone"
                    name="support_phone"
                    label="Support Phone"
                    placeholder="+1 (800) 555-0100"
                    value={tenantForm.support_phone}
                    onChange={(val) => handleTenantChange("support_phone", val)}
                  />
                </div>
              </div>

              {/* Submit Action */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={loading || subdomainStatus.checking || subdomainStatus.available === false || logoUploading}
                  className="w-full h-11 rounded-xl bg-gradient-to-r from-[#115B9D] via-[#0E528F] to-[#0A3E6E] hover:from-[#0E528F] hover:to-[#08335C] text-white font-bold text-sm sm:text-base shadow-lg shadow-[#115B9D]/25 hover:shadow-xl hover:shadow-[#115B9D]/35 active:scale-[0.99] transition-all duration-200 flex items-center justify-center gap-2 group cursor-pointer disabled:opacity-75 disabled:cursor-not-allowed"
                >
                  <span>
                    {loading ? "Registering Portal..." : "Create Business Portal"}
                  </span>
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            </form>

            {/* Back to Login */}
            <div className="text-center pt-3 pb-1">
              <span className="text-xs sm:text-sm text-slate-600">
                Already have an account?{" "}
              </span>
              <Link
                to="/login"
                className="text-xs sm:text-sm font-bold text-[#115B9D] hover:underline"
              >
                Sign In
              </Link>
            </div>

            {/* Security Footer */}
            <div className="mt-4 pt-3 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-1.5 text-[11px] text-slate-700">
              <span className="flex items-center gap-1.5 font-semibold text-slate-800">
                <Lock className="w-3.5 h-3.5 text-[#115B9D]" /> 256-Bit SSL Encrypted
              </span>
              <span className="font-semibold text-slate-800">
                Secure Portal Registration
              </span>
            </div>

            <p className="text-center text-[11px] text-slate-600 mt-2 font-medium">
              &copy; {new Date().getFullYear()} Charles S. Smith & Associates. All rights reserved.
            </p>
          </div>
        </div>
      </div>

      {/* ─── DYNAMIC DOMAINS REGISTRATION SUCCESS MODAL ─── */}
      {successModalOpen && successData && (
        <div className="fixed inset-0 z-[99999] bg-slate-950/75 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6 overflow-y-auto">
          <div className="relative bg-white rounded-3xl shadow-2xl border border-slate-200 w-full max-w-xl my-auto p-6 sm:p-8 space-y-6 animate-in fade-in zoom-in-95 duration-200">
            {/* Celebration Header */}
            <div className="text-center space-y-2">
              <div className="w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-800 flex items-center justify-center mx-auto shadow-inner">
                <CheckCircle2 size={30} />
              </div>
              <h2 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
                Business Portals Activated!
              </h2>
              <p className="text-xs sm:text-sm text-slate-600 max-w-md mx-auto">
                Dynamic isolated domains have been generated for{" "}
                <strong className="text-slate-900 font-bold">{successData.company_name}</strong>.
              </p>
            </div>

            {/* Generated Domains Cards */}
            <div className="space-y-3.5">
              {/* 1. Tenant Admin Portal */}
              <div className="p-4 rounded-2xl bg-blue-50/60 border border-blue-200/90 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 text-xs font-black text-[#115B9D] uppercase tracking-wider">
                    <Building2 size={15} />
                    <span>Tenant Admin Portal (Your Team)</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(successData.adminUrl, "admin")}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold bg-white text-slate-700 hover:text-slate-900 border border-slate-200 shadow-xs hover:bg-slate-50 transition-colors"
                  >
                    {copiedAdmin ? (
                      <>
                        <Check size={12} className="text-emerald-700" />
                        <span className="text-emerald-700">Copied</span>
                      </>
                    ) : (
                      <>
                        <Copy size={12} />
                        <span>Copy</span>
                      </>
                    )}
                  </button>
                </div>
                <div className="font-mono text-xs sm:text-sm text-slate-900 font-bold break-all bg-white/80 p-2.5 rounded-xl border border-blue-100">
                  {successData.adminUrl}
                </div>
                <p className="text-[11px] text-slate-600">
                  Manage your organization, team members, customer inquiries, and subscriptions.
                </p>
              </div>

              {/* 2. Customer Storefront Portal */}
              <div className="p-4 rounded-2xl bg-emerald-50/60 border border-emerald-200/90 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 text-xs font-black text-emerald-800 uppercase tracking-wider">
                    <Globe size={15} />
                    <span>Customer Storefront Portal (Your Customers)</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(successData.frontendUrl, "frontend")}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold bg-white text-slate-700 hover:text-slate-900 border border-slate-200 shadow-xs hover:bg-slate-50 transition-colors"
                  >
                    {copiedFrontend ? (
                      <>
                        <Check size={12} className="text-emerald-700" />
                        <span className="text-emerald-700">Copied</span>
                      </>
                    ) : (
                      <>
                        <Copy size={12} />
                        <span>Copy</span>
                      </>
                    )}
                  </button>
                </div>
                <div className="font-mono text-xs sm:text-sm text-slate-900 font-bold break-all bg-white/80 p-2.5 rounded-xl border border-emerald-100">
                  {successData.frontendUrl}
                </div>
                <p className="text-[11px] text-slate-600">
                  Share this storefront link with your clients so they can browse equipment and request quotes.
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="pt-2 flex flex-col sm:flex-row items-center justify-end gap-2.5">
              <button
                type="button"
                onClick={() => {
                  setSuccessModalOpen(false);
                  navigate(`/login?tenant=${encodeURIComponent(successData.subdomain)}`);
                }}
                className="w-full sm:w-auto px-4 py-2.5 rounded-xl text-xs sm:text-sm font-bold text-slate-700 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 transition-colors"
              >
                Go to Sign In
              </button>
              <a
                href={`${successData.adminUrl}/login`}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-xl text-xs sm:text-sm font-bold text-white bg-[#115B9D] hover:bg-[#0e4b82] shadow-md shadow-[#115B9D]/20 transition-all"
              >
                <span>Launch Tenant Admin</span>
                <ExternalLink size={14} />
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
